import {
  useState,
  type HTMLInputTypeAttribute,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from "react"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  floatingDateEmptyValueClassName,
  floatingFieldControlClassName,
  floatingFieldLabelClassName,
  floatingFieldPlaceholder,
  floatingFieldShellClassName,
  isFloatingFieldActive,
} from "@/components/ui/floating-field-utils"
import { cn } from "@/lib/utils"

/**
 * Outlined floating label: label sits on the border line when active,
 * so the control stays normal field height (h-9).
 *
 * Admin inputs default to `labelVariant="floating"`. Local-state fields use
 * {@link FloatingTextField}. Search/filter bars should keep stacked labels
 * or `label={false}`.
 *
 * When inactive, the floating label acts as the placeholder — never show a
 * real placeholder (or native date format text) at the same time or they overlap.
 *
 * Helpers live in `floating-field-utils.ts` so this file only exports React
 * components (Fast Refresh).
 */
export const FloatingFieldShell = ({
  active,
  label,
  htmlFor,
  required,
  error,
  className,
  labelAlign = "center",
  children,
}: {
  active: boolean
  label: ReactNode
  htmlFor?: string
  required?: boolean
  error?: boolean
  className?: string
  /** Use `top` for multiline fields so the idle label sits on the first line. */
  labelAlign?: "center" | "top"
  children: ReactNode
}) => (
  <div
    className={cn(floatingFieldShellClassName, className)}
    data-floating-active={active ? "true" : "false"}
    data-error={error ? "true" : undefined}
  >
    <label
      htmlFor={htmlFor}
      className={floatingFieldLabelClassName(active, labelAlign)}
      data-error={error ? "true" : undefined}
    >
      {label}
      {required ? <span className="text-destructive"> *</span> : null}
    </label>
    {children}
  </div>
)

const isNativeDateType = (type?: string) =>
  type === "date" ||
  type === "datetime-local" ||
  type === "time" ||
  type === "month" ||
  type === "week"

type FloatingTextFieldProps = {
  id: string
  label: ReactNode
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: boolean
  multiline?: boolean
  type?: HTMLInputTypeAttribute
  className?: string
  controlClassName?: string
  rows?: number
  trailing?: ReactNode
  autoComplete?: string
  autoFocus?: boolean
  name?: string
  readOnly?: boolean
  min?: string | number
  max?: string | number
  maxLength?: number
  step?: string | number
  onKeyDown?: (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  onFocus?: () => void
  onBlur?: () => void
  ref?: Ref<HTMLInputElement | HTMLTextAreaElement>
}

/** Controlled Input/Textarea with the outlined floating label. */
export const FloatingTextField = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  disabled,
  error,
  multiline = false,
  type = "text",
  className,
  controlClassName,
  rows = 3,
  trailing,
  autoComplete,
  autoFocus,
  name,
  readOnly,
  min,
  max,
  maxLength,
  step,
  onKeyDown,
  onFocus,
  onBlur,
  ref,
}: FloatingTextFieldProps) => {
  const [focused, setFocused] = useState(false)
  const active = isFloatingFieldActive(focused, value)
  const resolvedPlaceholder = floatingFieldPlaceholder(active, placeholder)
  const handleFocus = () => {
    setFocused(true)
    onFocus?.()
  }
  const handleBlur = () => {
    setFocused(false)
    onBlur?.()
  }

  const field = (
    <FloatingFieldShell
      active={active}
      label={label}
      htmlFor={id}
      required={required}
      error={error}
      labelAlign={multiline ? "top" : "center"}
      className={cn(
        multiline ? "min-h-[5.5rem] items-stretch" : undefined,
        trailing ? "min-w-0 flex-1" : className
      )}
    >
      {multiline ? (
        <Textarea
          ref={ref as Ref<HTMLTextAreaElement>}
          id={id}
          value={value}
          disabled={disabled}
          required={required}
          rows={rows}
          maxLength={maxLength}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          name={name}
          readOnly={readOnly}
          placeholder={resolvedPlaceholder}
          className={cn(
            floatingFieldControlClassName,
            "h-auto min-h-[4.5rem] resize-y py-2.5",
            controlClassName
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Input
          ref={ref as Ref<HTMLInputElement>}
          id={id}
          type={type}
          value={value}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          name={name}
          readOnly={readOnly}
          min={min}
          max={max}
          step={step}
          placeholder={resolvedPlaceholder}
          className={cn(
            floatingFieldControlClassName,
            isNativeDateType(type)
              ? floatingDateEmptyValueClassName(active)
              : undefined,
            controlClassName
          )}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </FloatingFieldShell>
  )

  if (!trailing) return field
  return (
    <div className={cn("flex gap-2", className)}>
      {field}
      {trailing}
    </div>
  )
}
